/* eslint-disable react/jsx-props-no-spreading */
import Draft, {
  CompositeDecorator,
  ContentBlock,
  ContentState,
  DraftHandleValue,
  Editor,
  EditorState,
  Modifier,
  RichUtils,
  SelectionState,
} from 'draft-js';
import {
  MouseEvent, useCallback, useEffect, useMemo, useRef, useState,
} from 'react';
import { Trans, useTranslation } from 'react-i18next';
import htmlToDraft from 'html-to-draftjs';
import { differenceInCalendarDays } from 'date-fns';
import useClickOutsideListener from '../../../hooks/use-click-outside-listener.hooks';
import STRING_KEYS from '../../../language/keys';
import { KEYBOARD_KEYS } from '../../../consts/keyboard_keys';
import AutoModeTextEditorHeader
  from '../../../modules/application/components/outreach/text-editor-header/auto-mode-text-edtior-header/auto-mode-text-editor-header.component';
import ManualModeTextEditorHeader
  from '../../../modules/application/components/outreach/text-editor-header/manual-mode-text-edtior-header/manual-mode-text-editor-header.component';
import {
  PerfectButtonHoverVariants,
  PerfectButtonSize,
} from '../Buttons/perfect-base-button/perfect-base-button.enums';
import { useUpdateScheduledMessageMutation } from '../../../store/rtk-query/apis/sequence/hooks/update-scheduled-message.mutation-hook';
import { MessageDirectionEnum } from '../../../store/rtk-query/apis/outreach/enums/message-direction.enum';
import { MessageResponseDto } from '../../../store/rtk-query/apis/outreach/dto/response/message.response';
import { IconButtonVariants } from '../Buttons/icon-button/icon-button.enums';
import { outreachQuery } from '../../../store/rtk-query/apis/outreach/outreach.toolkit-api';
import { useCancelConversationSequenceMutation } from '../../../store/rtk-query/apis/sequence/hooks/cancel-conversation-sequence.mutation-hook';
import { THEME } from '../../../themes';
import { SequenceCreationStatusEnum } from '../../../store/rtk-query/apis/outreach/enums/sequence-creation-status.enum';
import { useCreateConversationSequence } from '../../../store/rtk-query/apis/sequence/hooks/create-conversation-sequence.mutation-hook';
import { SOCIAL_TYPES } from '../../../consts';
import { sequenceQuery } from '../../../store/rtk-query/apis/sequence/sequence.toolkit-api';
import { LinkedinTaskStatusEnum } from '../../../store/rtk-query/apis/outreach/enums/linkedin-task-status.enum';
import { useMyUserSelector } from '../../../store/rtk-query/apis/user/selectors/get-my-user.selector';
import AlertTextEditorHeader
  from '../../../modules/application/components/outreach/text-editor-header/alert-text-editor-header/alert-text-editor-header.component';
import {
  BodyEditorContainer,
  Divider,
  EditorActionsContainer,
  EditorWrapper,
  EmailContainer,
  FooterContainer,
  FutureLinkedinTaskActionButtonPlaceholder,
  FutureLinkedinTaskActionButtonPlaceholderIcon,
  HeaderWrapper,
  InfoComponentWrapper,
  MainContainer,
  MarkAsDoneButtonIcon,
  MarkAsDoneButton,
  SaveChangesButton,
  SaveChangesButtonIcon,
  SendIcon,
  SendMessageButton,
  SkeletonContainer,
  StandardAction,
  SubjectEditorContainer,
  ManualModeSendButtonContainer,
  SendMessageNowButton,
  FutureLinkedinTaskActionButtonContainer,
} from './perfect-text-editor.styled';
import InlineStyleControlsComponent from './text-editor-controls/inline-style-controls/inline-style-controls.component';
import TextAlignmentControlsComponent
  from './text-editor-controls/text-alignment-controls/text-alignment-controls.component';
import { PerfectTextEditorProps, SendMessageHandlerArguments } from './perfect-text-editor';
import { PerfectTextEditorBlockAlignment } from './perfect-text-editor.enums';
import { contentToHtml, newEntityLocationStrategy } from './perfect-text-editor.utils';
import {
  DRAFT_ALIGNMENT_CLASS_NAME,
  DRAFT_ALIGNMENT_STYLE_LIST,
  DRAFT_CHANGE_TYPE,
  DRAFT_HANDLE_VALUE,
  DRAFT_NOT_HANDLE_VALUE,
  EDITOR_COMMAND,
} from './perfect-text-editor.const';
import EmailsInput from './emails-input/emails-input.component';
import { OutreachEmail } from './emails-input/emails-input';
import { Mention } from './decorators/mention-decorator/mention/mention.component';
import { useMention } from './decorators/mention-decorator/mention-decorator.hook';
import { DECORATORS_NAMES, OPEN_MENTION_CHAR } from './decorators/decorators.consts';
import TokensControl from './text-editor-controls/custom-editor-controls/mentions-control/mentions-control.component';
import { MentionPlaceholder } from './decorators/mention-decorator/mention-placeholder/mention-placeholder.component';
import { MentionsList } from './decorators/mention-decorator/mention-list/mention-list.component';
import { MentionState } from './decorators/mention-decorator/mention-decorator';
import TextEditorSkeleton from './text-editor-skeleton/text-editor-skeleton.component';
import SendViaLinkedinMenu from './send-via-linkedin-menu/send-via-linkedin-menu.component';

const decorators = new CompositeDecorator([
  {
    strategy: newEntityLocationStrategy(DECORATORS_NAMES.MENTION),
    component: Mention,
  },
  {
    strategy: newEntityLocationStrategy(DECORATORS_NAMES.MENTION_PLACEHOLDER),
    component: MentionPlaceholder,
  },
]);

const PerfectTextEditor = ({
  showSubject,
  disableSendButton,
  isSendButtonLoading,
  defaultEmails,
  sendMessageHandler,
  markMessageAsSentHandler,
  alertActionButtonHandler,
  subject,
  message,
  resetKey,
  mentions,
  isAutoMode,
  sequenceLength,
  currentSequenceStep,
  infoComponent,
  automateFollowupsButtonClickHandler,
  conversation,
}: PerfectTextEditorProps): JSX.Element => {
  const { t: translate } = useTranslation();
  const [touched, setTouched] = useState(false);
  const [editorBodyState, setEditorBodyState] = useState<EditorState>(
    () => EditorState.createEmpty(decorators),
  );

  const [editorSubjectState, setEditorSubjectState] = useState<EditorState>(
    () => EditorState.createEmpty(),
  );

  const {
    mention: bodyMentionState,
    confirmMention: confirmBodyMention,
    mentionReference: bodyMentionReference,
    closeMention: closeBodyMention,
    openMention: openBodyMention,
  } = useMention({
    editorState: editorBodyState,
    setEditorState: setEditorBodyState,
  });

  const {
    mention: subjectMentionState,
    confirmMention: confirmSubjectMention,
    mentionReference: subjectMentionReference,
    closeMention: closeSubjectMention,
    openMention: openSubjectMention,
  } = useMention({
    editorState: editorSubjectState,
    setEditorState: setEditorSubjectState,
  });

  const [emails, setEmails] = useState<OutreachEmail[]>(defaultEmails || []);
  const [showSubjectEditor, setShowSubjectEditor] = useState(false);
  const [editorInFocus, setEditorInFocus] = useState(false);
  const [subjectEditorInFocus, setSubjectEditorInFocus] = useState(false);

  const [updateLinkedinTask, {
    isLoading: isUpdateLinkedinTaskLoading,
  }] = sequenceQuery.useUpdateLinkedinTaskMutation();

  const [cancelConversationSequence, { isSuccess: isCancelConversationSequenceSuccess }] = useCancelConversationSequenceMutation();
  const [createConversationSequence, { isLoading: isCreateConversationSequenceLoading }] = useCreateConversationSequence();
  const [getConversationById] = outreachQuery.useLazyGetConversationByIdQuery();
  const { data: currentUser } = useMyUserSelector();

  useEffect(() => {
    if (isCancelConversationSequenceSuccess) {
      if (conversation?.id) {
        getConversationById({ conversationId: conversation.id });
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [conversation?.id, isCancelConversationSequenceSuccess]);

  const [updateScheduledMessage, {
    isLoading: isUpdateScheduledMessageLoading,
    isSuccess: isUpdateScheduledMessageSuccess,
  }] = useUpdateScheduledMessageMutation();

  useEffect(() => {
    if (resetKey) {
      setEmails([]);
      setEditorBodyState(EditorState.createEmpty(decorators));
      setEditorSubjectState(EditorState.createEmpty(decorators));
      setEditorInFocus(false);
    }
  }, [resetKey]);

  useEffect(() => {
    if (message) {
      const blocksFromHtml = htmlToDraft(message);
      const { contentBlocks, entityMap } = blocksFromHtml;
      const contentState = ContentState.createFromBlockArray(contentBlocks, entityMap);
      setEditorBodyState(EditorState.createWithContent(contentState, decorators));
      setEditorInFocus(true);
    }
  }, [message]);

  useEffect(() => {
    if (subject) {
      setEditorSubjectState(EditorState.createWithContent(ContentState.createFromText(subject), decorators));
    }
  }, [subject]);

  const refEditorWrapper = useRef<HTMLDivElement>(null);
  const refBodyEditor = useRef<Editor>(null);
  const refSubjectEditor = useRef<Editor>(null);

  useEffect(() => {
    if (document.activeElement === refSubjectEditor.current?.editor) {
      setSubjectEditorInFocus(true);

      return;
    }

    if (document.activeElement === refBodyEditor.current?.editor && subjectEditorInFocus) {
      setSubjectEditorInFocus(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [document.activeElement]);

  const editorUnFocusHandler = () => {
    const subjectHasText = editorSubjectState.getCurrentContent().hasText();
    const bodyHasText = editorBodyState.getCurrentContent().hasText();
    if (!subjectHasText && !bodyHasText) {
      setEditorInFocus(false);
    }
  };

  useEffect(() => {
    if (defaultEmails) {
      setEmails(defaultEmails);
    }
  }, [defaultEmails]);

  const selectedEmail = useMemo(() => emails.find(({ selected }) => selected)?.value, [emails]);

  useClickOutsideListener(
    editorUnFocusHandler,
    refEditorWrapper.current as HTMLElement,
  );

  useEffect(() => {
    if (showSubject) {
      setShowSubjectEditor(editorInFocus);
    }

    if (!editorInFocus) {
      setShowSubjectEditor(false);
    }
  }, [showSubject, editorInFocus]);

  useEffect(() => {
    if (isUpdateScheduledMessageSuccess) {
      setTouched(false);
    }
  }, [isUpdateScheduledMessageSuccess]);

  const showSkeleton = useMemo(
    () => conversation?.sequenceCreationStatus === SequenceCreationStatusEnum.InProgress ||
      conversation?.sequenceCreationStatus === SequenceCreationStatusEnum.Failed,
    [conversation?.sequenceCreationStatus],
  );

  const applyInlineStyle = (inlineStyle: string) => {
    const newState = RichUtils.toggleInlineStyle(editorBodyState, inlineStyle);
    setEditorBodyState(newState);
  };

  const applyAlignmentStyle = (newStyle: string) => {
    const oldStyle = DRAFT_ALIGNMENT_STYLE_LIST.filter((style) => style !== newStyle);
    const currentContent = editorBodyState.getCurrentContent();
    const selection = editorBodyState.getSelection();
    const focusBlock = currentContent.getBlockForKey(selection.getFocusKey());
    const anchorBlock = currentContent.getBlockForKey(selection.getAnchorKey());
    const isBackward = selection.getIsBackward();

    const selectionMerge = {
      anchorOffset: 0,
      focusOffset: focusBlock.getLength(),
    };

    if (isBackward) {
      selectionMerge.anchorOffset = anchorBlock.getLength();
    }
    const finalSelection = selection.merge(selectionMerge);
    const finalContent = oldStyle.reduce((content, style) => Modifier.removeInlineStyle(content, finalSelection, style), currentContent);
    const modifiedContent = Modifier.applyInlineStyle(finalContent, finalSelection, newStyle);
    const nextEditorState = EditorState.push(editorBodyState, modifiedContent, DRAFT_CHANGE_TYPE);
    setEditorBodyState(nextEditorState);
  };

  const handleKeyCommand = (
    command: string,
    state: EditorState,
    mentionState: MentionState,
    closeMention: (state: EditorState) => void,
    setEditorState: (state: EditorState) => void,
  ) => {
    if (command === EDITOR_COMMAND.SPLIT_BLOCK && mentionState.isOpen) {
      return DRAFT_HANDLE_VALUE;
    }
    if (command === EDITOR_COMMAND.BACKSPACE) {
      closeMention(state);
    }
    const newState = RichUtils.handleKeyCommand(state, command);

    if (newState) {
      setEditorState(newState);

      return DRAFT_HANDLE_VALUE;
    }

    return DRAFT_NOT_HANDLE_VALUE;
  };

  const handleKeyCommandForBody = (command: string, state: EditorState) => {
    return handleKeyCommand(command, state, bodyMentionState, closeBodyMention, setEditorBodyState);
  };

  const handleKeyCommandForSubject = (command: string, state: EditorState) => {
    return handleKeyCommand(command, state, subjectMentionState, closeSubjectMention, setEditorSubjectState);
  };

  const blockStyleFn = (block: ContentBlock) => {
    let alignment = PerfectTextEditorBlockAlignment.Left;
    block.findStyleRanges((e) => {
      if (e.hasStyle(PerfectTextEditorBlockAlignment.Center)) {
        alignment = PerfectTextEditorBlockAlignment.Center;
      }
      if (e.hasStyle(PerfectTextEditorBlockAlignment.Right)) {
        alignment = PerfectTextEditorBlockAlignment.Right;
      }

      return true;
    }, () => '');

    return `${DRAFT_ALIGNMENT_CLASS_NAME}-${alignment.toLowerCase()}`;
  };

  const onBodyStateChange = (editorState: EditorState) => {
    if (!touched && editorState.getCurrentContent().getPlainText() !== editorBodyState.getCurrentContent().getPlainText()) {
      setTouched(true);
    }
    setEditorBodyState(editorState);
  };

  const onSubjectStateChange = (editorState: EditorState) => {
    setEditorSubjectState(editorState);
  };

  const resetEditor = () => {
    setEditorBodyState(EditorState.push(editorBodyState, ContentState.createFromText(''), 'delete-character'));
    setEditorSubjectState(EditorState.push(editorSubjectState, ContentState.createFromText(''), 'delete-character'));
    setEditorInFocus(false);
  };

  const sendOrMarkAsSentMessageHandler = (handler: (args: SendMessageHandlerArguments) => void) => {
    const bodyHtml = contentToHtml(editorBodyState.getCurrentContent());
    const bodyPlanText = editorBodyState.getCurrentContent().getPlainText();
    const subjectPlainText = editorSubjectState.getCurrentContent().getPlainText();

    handler({
      bodyHtml,
      bodyPlanText,
      emails: selectedEmail || '',
      subject: subjectPlainText,
      onSuccess: resetEditor,
    });
  };

  const markMessageAsSent = () => {
    sendOrMarkAsSentMessageHandler(markMessageAsSentHandler);
  };

  const sendMessage = (e?: MouseEvent<HTMLButtonElement>) => {
    e?.stopPropagation();
    sendOrMarkAsSentMessageHandler(sendMessageHandler);
  };

  const onUpdateScheduledMessageButtonClick = (e?: MouseEvent<HTMLButtonElement>) => {
    e?.stopPropagation();

    if (!conversation?.nextScheduledMessage?.id) {
      return;
    }

    const subjectPlainText = editorSubjectState.getCurrentContent().getPlainText();
    const bodyHtml = contentToHtml(editorBodyState.getCurrentContent());

    updateScheduledMessage({
      messageId: conversation?.nextScheduledMessage?.id,
      subject: subjectPlainText,
      bodyHtml,
    });
  };

  const editorFocus = () => {
    refBodyEditor?.current?.focus();
  };

  const onClickEditorContainerHandler = (e: MouseEvent<HTMLElement>) => {
    e.stopPropagation();
    editorFocus();
    setEditorInFocus(true);
  };

  const isEditorDataValid = (validateEmail?: boolean) => {
    const subjectHasText = editorSubjectState.getCurrentContent().hasText();
    const bodyHasText = editorBodyState.getCurrentContent().hasText();
    if (validateEmail && !selectedEmail) {
      return false;
    }
    if (showSubject) {
      return bodyHasText && subjectHasText;
    }

    return bodyHasText;
  };

  const isSendMessageButtonActive = useMemo(() => {
    if (disableSendButton) {
      return false;
    }

    return isEditorDataValid(true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [disableSendButton, editorBodyState, editorSubjectState, selectedEmail, showSubject]);

  const isSendMessageNowButtonActive = useMemo(() => {
    const validateEmailAddress = !!conversation?.nextScheduledMessage;

    return isEditorDataValid(validateEmailAddress);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [editorBodyState, editorSubjectState, selectedEmail, showSubject, conversation?.nextScheduledMessage]);

  const handleBeforeInput = (
    chars: string,
    editorState: EditorState,
    openMention: (editorState: EditorState) => void,
  ) => {
    if (chars === OPEN_MENTION_CHAR && mentions) {
      openMention(editorState);

      return DRAFT_HANDLE_VALUE as DraftHandleValue;
    }

    return DRAFT_NOT_HANDLE_VALUE as DraftHandleValue;
  };

  const handleTokenControl = useCallback(() => {
    if (!editorInFocus) {
      return;
    }
    if (subjectEditorInFocus) {
      openSubjectMention(editorSubjectState);
    } else {
      setEditorInFocus(true);
      editorFocus();
      openBodyMention(editorBodyState);
    }
  }, [editorInFocus, subjectEditorInFocus, openSubjectMention, editorSubjectState, openBodyMention, editorBodyState]);

  const onCloseMentionsMenuHandler = (
    editorState: EditorState,
    setEditorState: (editorState: EditorState) => void,
    closeMention: (editorState: EditorState) => void,
  ) => {
    const currentContentState = editorState.getCurrentContent();
    const contentState = editorState.getCurrentContent();
    const selectionState = editorState.getSelection();
    const startKey = selectionState.getStartKey();
    const contentBlock = contentState.getBlockForKey(startKey);

    let entitySelection: SelectionState | null = null;
    contentBlock.findEntityRanges(
      (character) => {
        const entityKey = character.getEntity();

        return (
          entityKey !== null &&
          currentContentState.getEntity(entityKey)
            .getType() === DECORATORS_NAMES.MENTION_PLACEHOLDER
        );
      },
      (start, end) => {
        entitySelection = selectionState.merge({
          anchorOffset: start,
          focusOffset: end,
        });
      },
    );

    if (entitySelection) {
      const newContentState = Modifier.removeRange(
        contentState,
        entitySelection,
        'backward',
      );

      const newEditorState = EditorState.push(
        editorBodyState,
        newContentState,
        'remove-range',
      );

      setEditorState(newEditorState);
      closeMention(newEditorState);
    }
  };

  const onCopyEditorText = async () => {
    if (navigator?.clipboard) {
      const copiedText = await navigator.clipboard.readText();
      await navigator.clipboard.writeText(copiedText);
    }
  };

  // eslint-disable-next-line @typescript-eslint/ban-ts-comment
  // @ts-ignore
  const editorKeyboardEventHandler = (event) => {
    if ((event.metaKey || event.ctrlKey) && event.key === KEYBOARD_KEYS.ENTER) {
      if (isSendMessageButtonActive) {
        sendMessage();

        return null;
      }
    }

    return Draft.getDefaultKeyBinding(event);
  };

  const onMarkLinkedinTaskAsDoneButtonClick = (e?: MouseEvent<HTMLButtonElement>) => {
    e?.stopPropagation();
    const task = conversation?.nextOpenLinkedinTask;

    if (!task) {
      return;
    }

    resetEditor();

    updateLinkedinTask({
      positionId: conversation.positionId,
      conversationId: conversation.id,
      status: LinkedinTaskStatusEnum.Done,
      currentUser,
      task,
    });
  };

  const onSendMessageNowButtonClick = (e: MouseEvent<HTMLElement>) => {
    e.stopPropagation();
    if (conversation?.nextOpenLinkedinTask) {
      if (!selectedEmail) {
        onMarkLinkedinTaskAsDoneButtonClick();
      } else {
        sendMessage();
      }
    } else if (conversation?.nextScheduledMessage && selectedEmail) {
      sendMessage();
    }
  };

  const onCancelCampaignButtonClick = () => {
    if (conversation?.id) {
      cancelConversationSequence({ conversationId: conversation.id, positionId: conversation.positionId });
    }
  };

  const nextLinkedinTaskSendTimeDifferenceInDays = useMemo(() => {
    const sendAt = conversation?.nextOpenLinkedinTask?.sendAt || conversation?.nextScheduledMessage?.sendAt;

    return sendAt ? differenceInCalendarDays(new Date(sendAt), new Date()) : undefined;
  }, [conversation?.nextOpenLinkedinTask, conversation?.nextScheduledMessage]);

  const isNextLinkedinTaskIsInFuture = useMemo(() => {
    return nextLinkedinTaskSendTimeDifferenceInDays !== undefined && nextLinkedinTaskSendTimeDifferenceInDays > 0;
  }, [nextLinkedinTaskSendTimeDifferenceInDays]);

  const errorAlertBackgroundColor = useMemo(() => isNextLinkedinTaskIsInFuture ? THEME.COLORS.PRIMARY_DARK : THEME.COLORS.ACCENT, [isNextLinkedinTaskIsInFuture]);

  const renderAutoModeHeader = () => {
    if (conversation?.nextOpenLinkedinTask && !selectedEmail) {
      return (
        <AlertTextEditorHeader
          label={ translate(STRING_KEYS.OUTREACH_PAGE.EDITOR.ALERT_HEADER.LABEL) }
          infoText={ translate(STRING_KEYS.OUTREACH_PAGE.EDITOR.ALERT_HEADER.INFO_TEXT) }
          actionButtonHandler={ alertActionButtonHandler }
          backgroundColor={ errorAlertBackgroundColor }
        />
      );
    }

    const completedSteps = currentSequenceStep ? currentSequenceStep - 1 : 0;

    return (
      <AutoModeTextEditorHeader
        totalSteps={ sequenceLength || 0 }
        completedSteps={ completedSteps }
        cancelCampaignClickHandler={ onCancelCampaignButtonClick }
        scheduledMessageTime={ conversation?.nextScheduledMessage?.sendAt }
      />
    );
  };

  const showTextEditorHeader = useMemo(
    () => conversation?.nextScheduledMessage || (isAutoMode && conversation?.nextOpenLinkedinTask),
    [conversation?.nextOpenLinkedinTask, conversation?.nextScheduledMessage, isAutoMode]);

  const latestSentMessage = useMemo(() => conversation?.messageHistory?.slice().reverse()?.find(
    (messageHistory: MessageResponseDto) => messageHistory.messageDirection === MessageDirectionEnum.Outbound,
  ), [conversation?.messageHistory]);

  const showEmailContainer = useMemo(
    () => {
      return (
        (!isAutoMode && (!conversation?.nextScheduledMessage || !conversation?.messageHistory?.length || conversation?.nextOpenLinkedinTask))
      );
    },
    [conversation?.messageHistory?.length, conversation?.nextOpenLinkedinTask, conversation?.nextScheduledMessage, isAutoMode],
  );

  const renderManualModeHeader = () => {
    return (
      <ManualModeTextEditorHeader
        lastMessageSendTime={ latestSentMessage?.actionTime ? new Date(latestSentMessage?.actionTime) : undefined }
        automateFollowupsButtonClickHandler={ () => automateFollowupsButtonClickHandler?.() }
      />
    );
  };

  const Header = useMemo(() => {
    if (!showTextEditorHeader) {
      return null;
    }

    if (isAutoMode) {
      return renderAutoModeHeader();
    }

    return renderManualModeHeader();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAutoMode, conversation?.nextOpenLinkedinTask, conversation?.nextScheduledMessage, showTextEditorHeader]);

  const renderSendMessageButton = () => {
    return (
      <SendMessageButton
        variant={ IconButtonVariants.Fill }
        Icon={ SendIcon }
        onClick={ sendMessage }
        disabled={ !isSendMessageButtonActive }
        isLoading={ isSendButtonLoading || isUpdateLinkedinTaskLoading }
      />
    );
  };

  const renderMarkLinkedinTaskAsDoneActionButton  = () => {
    return (
      <MarkAsDoneButton
        hoverVariant={ PerfectButtonHoverVariants.Grow }
        size={ PerfectButtonSize.Small }
        onClick={ onMarkLinkedinTaskAsDoneButtonClick }
        Icon={ MarkAsDoneButtonIcon }
        isLoading={ isUpdateLinkedinTaskLoading }
      >
        {translate(STRING_KEYS.OUTREACH_PAGE.EDITOR.MARK_AS_DONE_BUTTON_TEXT)}
      </MarkAsDoneButton>
    );
  };

  const renderSaveScheduledMessageButton = () => {
    return (
      <SaveChangesButton
        onClick={ onUpdateScheduledMessageButtonClick }
        Icon={ SaveChangesButtonIcon }
        size={ PerfectButtonSize.Medium }
        hoverVariant={ PerfectButtonHoverVariants.Grow }
        disabled={ !touched }
        isLoading={ isUpdateScheduledMessageLoading }
      >
        { translate(STRING_KEYS.OUTREACH_PAGE.EDITOR.SAVE_CHANGES_BUTTON_TEXT) }
      </SaveChangesButton>
    );
  };

  const renderFutureLinkedinTaskActionButtons = (renderActionButton?: boolean) => {
    return (
      <FutureLinkedinTaskActionButtonContainer>
        <FutureLinkedinTaskActionButtonPlaceholder>
          <FutureLinkedinTaskActionButtonPlaceholderIcon />
          <div>
            <Trans
              i18nKey={ STRING_KEYS.OUTREACH_PAGE.EDITOR.FUTURE_TASK_INFO_TEXT }
              values={ { daysCount: nextLinkedinTaskSendTimeDifferenceInDays } }
            />
          </div>
        </FutureLinkedinTaskActionButtonPlaceholder>
        { renderActionButton && (
          <SendMessageNowButton
            onClick={ onSendMessageNowButtonClick }
            Icon={ SendIcon }
            size={ PerfectButtonSize.Small }
            hoverVariant={ PerfectButtonHoverVariants.Grow }
            disabled={ disableSendButton || !isSendMessageNowButtonActive }
            isLoading={ (!selectedEmail && isUpdateLinkedinTaskLoading) || isSendButtonLoading }
          >
            {translate(selectedEmail ? STRING_KEYS.OUTREACH_PAGE.EDITOR.SEND_MESSAGE_NOW_BUTTON_TEXT : STRING_KEYS.OUTREACH_PAGE.EDITOR.MARK_AS_DONE_BUTTON_TEXT)}
          </SendMessageNowButton>
        ) }
      </FutureLinkedinTaskActionButtonContainer>
    );
  };

  const renderAutoModeActionButton = () => {
    if (conversation?.nextScheduledMessage) {
      return renderSaveScheduledMessageButton();
    }

    if (conversation?.nextOpenLinkedinTask) {
      if (isNextLinkedinTaskIsInFuture) {
        return renderFutureLinkedinTaskActionButtons();
      }

      return renderMarkLinkedinTaskAsDoneActionButton();
    }

    return renderSendMessageButton();
  };

  const onSendViaLinkedinMenuButtonClick = async () => {
    if (navigator?.clipboard) {
      await navigator.clipboard.writeText(editorBodyState.getCurrentContent().getPlainText());
    }
  };

  const renderManualModeActionButton = () => {
    if ((conversation?.nextOpenLinkedinTask || conversation?.nextScheduledMessage) && isNextLinkedinTaskIsInFuture) {
      return renderFutureLinkedinTaskActionButtons(true);
    }

    if (conversation?.nextOpenLinkedinTask && !selectedEmail) {
      return renderMarkLinkedinTaskAsDoneActionButton();
    }

    return (
      <ManualModeSendButtonContainer>
        { conversation?.nextScheduledMessage && (
          <SendViaLinkedinMenu
            talentName={ conversation?.match.talent.firstName || '' }
            talentLinkedinUrl={ (conversation?.match?.talent?.socialLinks?.find((socialLink) => socialLink.type === SOCIAL_TYPES.LINKEDIN))?.url || '' }
            menuButtonClickHandler={ onSendViaLinkedinMenuButtonClick }
            markMessageAsSentButtonClickHandler={ markMessageAsSent }
          />
        ) }
        { renderSendMessageButton() }
      </ManualModeSendButtonContainer>
    );
  };

  const ActionButton = isAutoMode ? renderAutoModeActionButton() : renderManualModeActionButton();

  const renderSkeleton = () => {
    return (
      <SkeletonContainer>
        <TextEditorSkeleton
          showAnimation={
            isCreateConversationSequenceLoading ||
            conversation?.sequenceCreationStatus === SequenceCreationStatusEnum.InProgress
          }
          hasError={ conversation?.sequenceCreationStatus === SequenceCreationStatusEnum.Failed }
          errorChipButtonClickHandler={ () => conversation?.id && createConversationSequence({ conversationId: conversation?.id }) }
          isErrorActionLoading={ isCreateConversationSequenceLoading }
        />
      </SkeletonContainer>
    );
  };

  const renderEditor = () => {
    if (showSkeleton) {
      return renderSkeleton();
    }

    return (
      <div>
        <SubjectEditorContainer
          isVisible={ showSubjectEditor }
          onClick={ (e) => e.stopPropagation() }
        >
          <Editor
            ref={ refSubjectEditor }
            editorState={ editorSubjectState }
            onChange={ onSubjectStateChange }
            handleKeyCommand={ handleKeyCommandForSubject }
            keyBindingFn={ (e) => {
              if (!e.metaKey && e.code === KEYBOARD_KEYS.ENTER) {
                return null;
              }

              return Draft.getDefaultKeyBinding(e);
            } }
            handleBeforeInput={ (chars: string, editorState: EditorState) => {
              return handleBeforeInput(chars, editorState, openSubjectMention);
            } }
            onCopy={ onCopyEditorText }
            placeholder={ translate(STRING_KEYS.OUTREACH_PAGE.EDITOR.SUBJECT.PLACEHOLDER) }
          />
          <Divider />
        </SubjectEditorContainer>
        <BodyEditorContainer>
          <Editor
            ref={ refBodyEditor }
            keyBindingFn={ editorKeyboardEventHandler }
            blockStyleFn={ blockStyleFn }
            editorState={ editorBodyState }
            handleKeyCommand={ handleKeyCommandForBody }
            onChange={ onBodyStateChange }
            onCopy={ onCopyEditorText }
            handleBeforeInput={ (chars: string, editorState: EditorState) => {
              return handleBeforeInput(chars, editorState, openBodyMention);
            } }
            placeholder={ translate(STRING_KEYS.OUTREACH_PAGE.EDITOR.MESSAGE.PLACEHOLDER) }
          />
        </BodyEditorContainer>
      </div>
    );
  };

  const renderInfoComponent = () => {
    if (infoComponent && !showSkeleton) {
      return <InfoComponentWrapper>{ infoComponent }</InfoComponentWrapper>;
    }

    return <Divider />;
  };

  return (
    <MainContainer ref={ refEditorWrapper } >
      { showEmailContainer && (
        <EmailContainer isVisible={ editorInFocus }>
          <EmailsInput
            emails={ emails }
            setEmailsHandler={ setEmails }
            alertActionButtonHandler={ () => alertActionButtonHandler?.() }
            errorAlertBackgroundColor={ isNextLinkedinTaskIsInFuture ? THEME.COLORS.PRIMARY_DARK : undefined }
          />
        </EmailContainer>
      ) }
      <div>
        { Header && <HeaderWrapper>{ Header }</HeaderWrapper> }
        <EditorWrapper
          inFocus={ editorInFocus }
          showTopBorderRadius={ !showTextEditorHeader }
          onClick={ onClickEditorContainerHandler }
        >
          { renderEditor() }
          <FooterContainer>
            { renderInfoComponent() }
            <EditorActionsContainer>
              <StandardAction>
                <InlineStyleControlsComponent
                  toggleHandler={ applyInlineStyle }
                  currentInlineStyle={ editorBodyState.getCurrentInlineStyle() }
                />
                <TextAlignmentControlsComponent
                  toggleHandler={ applyAlignmentStyle }
                  currentInlineStyle={ editorBodyState.getCurrentInlineStyle() }
                />
              </StandardAction>
              <StandardAction>
                <TokensControl
                  disabled={ !editorInFocus || showSkeleton }
                  toggleHandler={ handleTokenControl }
                />
              </StandardAction>
              { ActionButton }
            </EditorActionsContainer>
          </FooterContainer>
          { mentions && (
            <MentionsList
              mentions={ mentions }
              mentionState={ bodyMentionState }
              mentionReference={ bodyMentionReference }
              confirmMentionHandler={ confirmBodyMention }
              mentionsMenuCloseHandler={ () => onCloseMentionsMenuHandler(editorBodyState, setEditorBodyState, closeBodyMention) }
            />
          )}
          { mentions && (
            <MentionsList
              mentions={ mentions }
              mentionState={ subjectMentionState }
              mentionReference={ subjectMentionReference }
              confirmMentionHandler={ confirmSubjectMention }
              mentionsMenuCloseHandler={ () => onCloseMentionsMenuHandler(editorSubjectState, setEditorSubjectState, closeSubjectMention) }
            />
          )}
        </EditorWrapper>
      </div>
    </MainContainer>
  );
};

export default PerfectTextEditor;

PerfectTextEditor.defualtProps = {
  disableSendButton: false,
  emailsInput: [],
};
